import ReadMore from '@fawazahmed/react-native-read-more'
import {
  Attachment,
  Conversation,
  Emoji,
  Id,
  Message,
  MessageAction,
  User,
  monthDateTimeString,
} from '@nextforce/api'
import { Icon, Text, makeStyles, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { font } from 'app/theme'
import { NEWSFEED_CARD_PADDING } from 'chat/shared/constants'
import {
  getMessageGroupData,
  useNewsfeedMessageImageStyles,
} from 'chat/shared/util'
import { Image } from 'expo-image'
import { uniq } from 'lodash'
import moment from 'moment'
import React, {
  ReactNode,
  RefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  TextStyle,
  View,
  useWindowDimensions,
} from 'react-native'
import {
  IMessage as ChatMessage,
  GiftedChat,
  IMessage,
  MessageProps,
} from 'react-native-gifted-chat'
import { AnimatedList } from 'react-native-gifted-chat/src/MessagesContainer'
import ParsedText from 'react-native-parsed-text'
import { Menu, MenuProvider } from 'react-native-popup-menu'
import { SafeAreaProvider, SafeAreaView } from 'react-native-safe-area-context'
import {} from 'react-native-web'
import ActionSheetMenu from 'shared/components/chat/action_sheet_menu'
import { NonSelectableText } from 'shared/components/chat/non_selectable_text'
import { FloatingActionButton } from 'shared/components/fab'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { UserAvatar } from 'shared/components/user/user_avatar'
import { WEB_SCREEN_WIDTH } from 'shared/constants'
import { stripInvalidCodepoints } from 'shared/utils'
import { isPdfAttachment } from '../file/screen'
import { extractUrl } from '../queries/shared/utils'
import { NewsfeedLinkPreviewComponent } from './link_preview'
import { PinnedButton } from './pinActionButton'

const POSITIONS_MAX_COUNT = 2
const MAX_MEDIA_SHOWN_PER_ATTACHMENT = 2
const MIN_ITEMS_COUNT = 12

interface Props {
  user: User | undefined
  conversation: Conversation
  chatMessages: ChatMessage[]
  pinnedChatMessages: ChatMessage[]
  readByCounts?: {
    [messageId: string]: number
  }
  messageIndex: number | undefined
  onSendMessage(): Promise<void>
  onShowFile(attachment: Attachment, isPinnedMessage?: boolean): void
  onShowReactors(messageId: string, emoji: Emoji): void
  onHideDescription(): void
  onSendReaction(id: string, emoji: Emoji): void
  onClearScrolling(): void
  onShowMultiAttachments: (messageId: string, attachmentIndex: number) => void
  onLongPress: (context: any, message?: Message) => void
  parsePatterns: (linkStyle: TextStyle) => any
  getMessage(id?: Id): Message | undefined
  getPinnedMessage(id?: Id): Message | undefined
  onCreateComposeMessage(): void
  getMessageGroup: (id?: Id) => Message[] | undefined
  getPinnedMessageGroup: (id?: Id) => Message[] | undefined
  onLinkPress: (url: string) => void
  onMessageAction: (message: Message, action: MessageAction) => void
  getAllowedActions: (
    message: Message,
    isPinnedMessage?: boolean
  ) => MessageAction[] | undefined
  isLoadingEarlier: boolean
  hasMoreMessages: boolean | undefined
  onLoadEarlier: () => void
}

export function NewsfeedMessagesView({
  conversation,
  chatMessages,
  pinnedChatMessages,
  messageIndex,
  parsePatterns,
  onLongPress,
  onClearScrolling,
  onHideDescription,
  onSendMessage,
  onSendReaction,
  onShowFile,
  onShowMultiAttachments,
  onShowReactors,
  user,
  getMessage,
  getPinnedMessage,
  readByCounts,
  onCreateComposeMessage,
  getMessageGroup,
  getPinnedMessageGroup,
  onLinkPress,
  onMessageAction,
  getAllowedActions,
  hasMoreMessages,
  isLoadingEarlier,
  onLoadEarlier,
}: Props) {
  const { theme } = useTheme()
  const styles = useStyles()
  const [showPinned, setShowPinned] = useState(false)
  const [selectedPinnedMessage, setSelectedPinnedMessage] = useState<
    Message | undefined
  >()

  const description = useMemo(() => {
    if (
      !conversation ||
      conversation.descriptionHidden ||
      !conversation.description ||
      conversation.description.trim().length === 0
    ) {
      return null
    }

    return conversation.description
  }, [conversation])

  const renderMessage = useCallback(
    (props: Readonly<MessageProps<ChatMessage>>, isPinnedMessage = false) => {
      const getCurrentMessageGroup = isPinnedMessage
        ? getPinnedMessageGroup
        : getMessageGroup
      const onLongPressMessage = (message?: Message) => {
        if (isPinnedMessage) {
          if (!message) {
            return
          }
          setSelectedPinnedMessage(message)
          if (pinnedMenuRef.current) {
            pinnedMenuRef.current.open().catch(() => undefined)
          }
        } else {
          onLongPress(null, message)
        }
      }
      const messageGroup = getCurrentMessageGroup(
        props.currentMessage?._id.toString()
      )
      if (messageGroup) {
        const firstMessage = messageGroup[0]

        const readByCount =
          readByCounts && readByCounts[firstMessage.id]
            ? readByCounts[firstMessage.id]
            : undefined

        return (
          <NewsfeedMessage
            messageGroup={messageGroup}
            memberCount={conversation?.memberCount}
            readByCount={readByCount}
            showReadByCount={props.currentMessage?.user._id === user?.id}
            onLongPressMessage={() => onLongPressMessage(firstMessage)}
            onReactMessageAction={onSendReaction}
            onShowReactors={onShowReactors}
            parsePatterns={parsePatterns}
            onShowMultiAttachments={onShowMultiAttachments}
            onAttachmentPress={attachment =>
              onShowFile(attachment, isPinnedMessage)
            }
            onLinkPress={onLinkPress}
          />
        )
      }
      return <></>
    },
    [
      getPinnedMessageGroup,
      getMessageGroup,
      onLongPress,
      readByCounts,
      conversation?.memberCount,
      user?.id,
      onSendReaction,
      onShowReactors,
      parsePatterns,
      onShowMultiAttachments,
      onShowFile,
      onLinkPress,
    ]
  )

  const renderPinnedMessage = useCallback(
    (props: Readonly<MessageProps<ChatMessage>>) => {
      return renderMessage(props, true)
    },
    [renderMessage]
  )

  const renderEmptyFooter = useCallback(() => {
    return <View style={styles.emptyFooter} />
  }, [styles.emptyFooter])

  const onPinnedChatMessageLongPress = useCallback(
    (context: any, chatMessage?: ChatMessage) => {
      if (!chatMessage) {
        return
      }
      const message = getPinnedMessage(chatMessage._id.toString())
      if (!message) {
        return
      }
      setSelectedPinnedMessage(message)
      if (pinnedMenuRef.current) {
        pinnedMenuRef.current.open().catch(() => null)
      }
    },
    [getPinnedMessage]
  )

  const onChatMessageLongPress = useCallback(
    (context: any, chatMessage?: ChatMessage) => {
      if (!chatMessage) {
        return
      }
      const message = getMessage(chatMessage._id.toString())
      onLongPress(context, message)
    },
    [getMessage, onLongPress]
  )

  const ref = useRef<AnimatedList<IMessage>>(null)
  const pinnedMenuRef = useRef<Menu | null>(null)
  const readOnly = conversation?.postsAllowed === 'none'

  return (
    <View style={styles.newsfeedContainer}>
      <View style={styles.pinnedButtonContainer}>
        {description ? (
          <View style={styles.descriptionBox}>
            <Text style={styles.descriptionText}>{description}</Text>
            <PressableOpacity onPress={onHideDescription}>
              <Text style={styles.descriptionCloseButton}>x</Text>
            </PressableOpacity>
            {!showPinned &&
              pinnedChatMessages.length > 0 &&
              conversation.type !== 'dm' && (
                <PinnedButton
                  setShowPinned={setShowPinned}
                  descriptionHidden={!description}
                />
              )}
          </View>
        ) : (
          !showPinned &&
          pinnedChatMessages.length >= 0 &&
          conversation.type !== 'dm' && (
            <PinnedButton
              setShowPinned={setShowPinned}
              descriptionHidden={!description}
            />
          )
        )}
      </View>

      {showPinned && user ? (
        <Modal
          visible={showPinned}
          animationType="slide"
          transparent
          onRequestClose={() => setShowPinned(false)}
        >
          <SafeAreaProvider>
            <MenuProvider skipInstanceCheck>
              <SafeAreaView style={styles.pinnedMessagesModal}>
                <ActionSheetMenu
                  menuRef={pinnedMenuRef}
                  actions={
                    selectedPinnedMessage
                      ? getAllowedActions(selectedPinnedMessage, true)
                      : undefined
                  }
                  onAction={action => {
                    if (selectedPinnedMessage) {
                      onMessageAction(selectedPinnedMessage, action)
                      setSelectedPinnedMessage(undefined)
                    }
                  }}
                  onClose={() => setSelectedPinnedMessage(undefined)}
                />
                <View style={[styles.pinnedMessagesModalContainer]}>
                  <View style={styles.pinnedMessagesHeader}>
                    <Text
                      style={styles.pinnedMessagesHeaderText}
                    >{`${stripInvalidCodepoints(conversation.name)} : Pinned`}</Text>
                    <Icon
                      name="times"
                      type="font-awesome-5"
                      onPress={() => setShowPinned(false)}
                      color={theme.colors?.primary}
                      solid
                    />
                  </View>
                  <GiftedChat
                    messages={pinnedChatMessages}
                    messagesContainerStyle={styles.pinnedChatMessages}
                    user={{
                      _id: user.id,
                      name: `${user.firstName} ${user.lastName}`,
                      avatar: user.photoUrl ?? undefined,
                    }}
                    renderMessage={renderPinnedMessage}
                    isAlignedTop={false}
                    renderInputToolbar={() => <></>}
                    minComposerHeight={0}
                    maxComposerHeight={0}
                    minInputToolbarHeight={0}
                    onLongPressMessage={onPinnedChatMessageLongPress}
                    renderChatEmpty={() => (
                      <View style={styles.emptyListContainer}>
                        <Text style={styles.errorMessage}>
                          {t('noPinnedMessages')}
                        </Text>
                      </View>
                    )}
                  />
                </View>
              </SafeAreaView>
            </MenuProvider>
          </SafeAreaProvider>
        </Modal>
      ) : null}

      {user ? (
        <GiftedChat
          messagesContainerRef={ref as RefObject<AnimatedList<IMessage>>}
          listProps={{
            scrollIndicatorInsets: { right: 1 },
            initialNumToRender:
              messageIndex ?? 0 < MIN_ITEMS_COUNT
                ? MIN_ITEMS_COUNT
                : messageIndex,
            onScrollToIndexFailed: (error: {
              averageItemLength: number
              index: number
            }) => {
              setTimeout(
                () =>
                  ref.current?.scrollToIndex({
                    index: error.index,
                    viewPosition: 0,
                  }),
                0
              )
              onClearScrolling()
            },
          }}
          messages={chatMessages}
          loadEarlierMessagesProps={{
            isAvailable: !!hasMoreMessages,
            isLoading: !!isLoadingEarlier,
            onPress: onLoadEarlier,
          }}
          user={{
            _id: user.id,
            name: `${user.firstName} ${user.lastName}`,
            avatar: user.photoUrl ?? undefined,
          }}
          onSend={onSendMessage}
          maxComposerHeight={300}
          renderInputToolbar={() => <View />}
          renderFooter={renderEmptyFooter}
          minInputToolbarHeight={0}
          onLongPressMessage={onChatMessageLongPress}
          renderMessage={renderMessage}
          renderChatEmpty={() => (
            <View style={styles.emptyListContainer}>
              <Text style={styles.errorMessage}>{t('noMessages')}</Text>
            </View>
          )}
        />
      ) : (
        <ActivityIndicator
          style={styles.messageLoadingIndicator}
          size="large"
          color="black"
        />
      )}
      {!readOnly ? (
        <FloatingActionButton
          onPress={onCreateComposeMessage}
          useBottomInset={false}
        />
      ) : null}
    </View>
  )
}
interface NewsfeedMessageProps {
  messageGroup: Message[]
  memberCount?: number
  parsePatterns: any
  readByCount: number | undefined
  showReadByCount: boolean
  onShowReactors(messageId: string, reaction: Emoji): void
  onLongPressMessage(): void
  onReactMessageAction(messageId: String, emoji: Emoji): void
  onShowMultiAttachments: (messageId: string, attachmentIndex: number) => void
  onAttachmentPress: (attachment: Attachment) => void
  onLinkPress: (url: string) => void
}

function NewsfeedMessage({
  memberCount,
  messageGroup,
  parsePatterns,
  readByCount,
  showReadByCount,
  onLongPressMessage,
  onShowReactors,
  onReactMessageAction,
  onShowMultiAttachments,
  onAttachmentPress,
  onLinkPress,
}: NewsfeedMessageProps) {
  const styles = useStyles()
  let url = ''
  const { attachments, reactions } = getMessageGroupData(messageGroup)
  const emojiTypes = uniq(reactions.map(reaction => reaction.emoji))
  const firstMessage = messageGroup[0]

  if (firstMessage.body && firstMessage.body.length > 0) {
    url = extractUrl(firstMessage.body)
  }
  const pattern = parsePatterns({
    color: styles.text.color,
    textDecorationLine: 'underline',
  })

  const getReactionCount = useCallback(
    (emoji: string) => {
      return reactions.reduce((sum, reaction) => {
        return sum + (reaction.emoji === emoji ? 1 : 0)
      }, 0)
    },
    [reactions]
  )

  const EmojisRow = useCallback(() => {
    return (
      <>
        {emojiTypes.map((emoji, index) => {
          return (
            <View key={index}>
              <Emojis
                reaction={emoji}
                count={getReactionCount(emoji)}
                messageId={firstMessage.id}
                onShowReactors={onShowReactors}
                onReactMessageAction={onReactMessageAction}
              />
            </View>
          )
        })}
      </>
    )
  }, [
    emojiTypes,
    firstMessage?.id,
    getReactionCount,
    onReactMessageAction,
    onShowReactors,
  ])

  const onPressMultiAttachMessage = useCallback(() => {
    if (firstMessage) {
      onShowMultiAttachments(firstMessage.id, 1)
    }
  }, [firstMessage, onShowMultiAttachments])

  return (
    <Pressable onLongPress={onLongPressMessage}>
      <View
        style={styles.container}
        onContextMenu={e => {
          e.preventDefault()
          onLongPressMessage()
        }}
      >
        <View style={styles.card}>
          {!!attachments ? (
            <NewsfeedMessageImages
              attachments={attachments}
              onLongPressMessage={onLongPressMessage}
              onPressMessageAttachment={onAttachmentPress}
              onPressMultiAttachMessage={onPressMultiAttachMessage}
            />
          ) : null}
          {!!url ? (
            <NewsfeedLinkPreviewComponent
              url={url}
              onLongPress={onLongPressMessage}
              onLinkPress={onLinkPress}
            />
          ) : null}
          {firstMessage.body ? (
            <View style={styles.messageBody}>
              <ReadMoreText
                key={firstMessage.id}
                message={firstMessage}
                parsePatterns={pattern}
                onMessageLongPress={onLongPressMessage}
              />
            </View>
          ) : null}
          <View style={styles.createdAtContainer}>
            <NonSelectableText style={styles.secondaryText}>
              {monthDateTimeString(moment(firstMessage.createdAt))}
            </NonSelectableText>
          </View>
          <View style={styles.avatarContainer}>
            <UserAvatar
              user={firstMessage.user}
              showAvatarIfNoPhone
              size={'medium'}
            />
            {firstMessage.user ? (
              <View style={styles.nameContainer}>
                <NonSelectableText style={styles.name}>
                  {stripInvalidCodepoints(
                    `${firstMessage.user.firstName} ${firstMessage.user.lastName}`
                  )}
                </NonSelectableText>
                <UserPositions userPositions={firstMessage.user.positions} />
              </View>
            ) : null}
          </View>
          <View style={styles.emojisAndReadByContainer}>
            <View style={styles.emojisContainer}>
              <EmojisRow />
            </View>
            <View style={styles.readByContainer}>
              {showReadByCount ? (
                <NonSelectableText style={styles.secondaryText}>{`Read By ${
                  readByCount ?? 0
                }/${memberCount}`}</NonSelectableText>
              ) : null}
            </View>
          </View>
        </View>
      </View>
    </Pressable>
  )
}

interface NewsfeedMessageImageProps {
  attachments: Attachment[]
  onLongPressMessage: () => void
  onPressMessageAttachment: (attachment: Attachment) => void
  onPressMultiAttachMessage: () => void
}
function NewsfeedMessageImages({
  attachments,
  onLongPressMessage,
  onPressMessageAttachment,
  onPressMultiAttachMessage,
}: NewsfeedMessageImageProps) {
  const styles = useStyles()
  const mediaAttachments = attachments.filter(
    attachment => !isPdfAttachment(attachment)
  )
  const documentAttachments = attachments.filter(attachment =>
    isPdfAttachment(attachment)
  )

  let mediaAttachmentsComponent = <></>
  if (mediaAttachments.length) {
    mediaAttachmentsComponent = (
      <>
        {mediaAttachments
          .slice(0, MAX_MEDIA_SHOWN_PER_ATTACHMENT)
          .map((attachment, index) => (
            <MessageImage
              key={index}
              attachment={attachment}
              onLongPressMessage={onLongPressMessage}
              onPressMessage={() => onPressMessageAttachment(attachment)}
              count={
                index === MAX_MEDIA_SHOWN_PER_ATTACHMENT - 1
                  ? mediaAttachments.length - 2
                  : undefined
              }
              onPressMultiAttachMessage={onPressMultiAttachMessage}
            />
          ))}
      </>
    )
  }

  let documentAttachmentsComponent = <></>
  if (documentAttachments.length) {
    documentAttachmentsComponent = (
      <View style={styles.documentAttachmentsContainer}>
        {documentAttachments.map((attachment, index) => (
          <MessageDocumentImage
            attachment={attachment}
            key={index}
            onPressMessage={() => onPressMessageAttachment(attachment)}
            onLongPressMessage={onLongPressMessage}
            size={
              index === documentAttachments.length - 1 && index % 2 === 0
                ? 'full'
                : 'half'
            }
          />
        ))}
      </View>
    )
  }
  return (
    <>
      <MediaAttachmentsContainer
        showLabel={!!(mediaAttachments.length && documentAttachments.length)}
      >
        {mediaAttachmentsComponent}
      </MediaAttachmentsContainer>
      <DocumentAttachmentsContainer
        showLabel={!!(mediaAttachments.length && documentAttachments.length)}
      >
        {documentAttachmentsComponent}
      </DocumentAttachmentsContainer>
    </>
  )
}

function MediaAttachmentsContainer({
  children,
  showLabel = false,
}: {
  children: ReactNode
  showLabel?: boolean
}) {
  const styles = useStyles()
  return (
    <View>
      {showLabel ? (
        <NonSelectableText style={styles.attachmentsLabel}>
          {t('media')}
        </NonSelectableText>
      ) : null}
      {children}
    </View>
  )
}

function DocumentAttachmentsContainer({
  children,
  showLabel = false,
}: {
  children: ReactNode
  showLabel?: boolean
}) {
  const styles = useStyles()
  return (
    <View>
      {showLabel ? (
        <NonSelectableText style={styles.attachmentsLabel}>
          {t('documents')}
        </NonSelectableText>
      ) : null}
      {children}
    </View>
  )
}

function MessageDocumentImage({
  attachment,
  onLongPressMessage,
  onPressMessage,
  size = 'full',
}: {
  attachment: Attachment
  onLongPressMessage: () => void
  onPressMessage: () => void
  size?: 'full' | 'half'
}) {
  const styles = useStyles()
  const dimensions = useWindowDimensions()
  return (
    <Pressable onPress={onPressMessage} onLongPress={onLongPressMessage}>
      <View key={attachment.uri} style={styles.messageDocumentImageContainer}>
        <Image
          source={{ uri: attachment.thumbnail?.url }}
          contentFit="cover"
          style={[
            useNewsfeedMessageImageStyles({
              width: dimensions.width / (size === 'full' ? 1 : 2),
              isMultiDocument: size === 'half',
            }),
          ]}
        />
        <View style={styles.documentNameContainer}>
          <NonSelectableText numberOfLines={3} textBreakStrategy="simple">
            {attachment.name?.length
              ? stripInvalidCodepoints(attachment.name)
              : 'PDF Document'}
          </NonSelectableText>
        </View>
      </View>
    </Pressable>
  )
}

function MessageImage({
  attachment,
  count,
  onLongPressMessage,
  onPressMultiAttachMessage,
  onPressMessage,
}: {
  attachment: Attachment
  count?: number
  onLongPressMessage: () => void
  onPressMessage: () => void
  onPressMultiAttachMessage?: () => void
}) {
  const dimensions = useWindowDimensions()
  const styles = useStyles()
  return (
    <PressableOpacity
      disabled={!count}
      onPress={onPressMultiAttachMessage}
      onLongPress={onLongPressMessage}
    >
      <Pressable
        disabled={!!count}
        onPress={onPressMessage}
        onLongPress={onLongPressMessage}
      >
        <View key={attachment.uri} style={styles.messageImageContainer}>
          <Image
            source={{ uri: attachment.thumbnail?.url }}
            contentFit="cover"
            style={useNewsfeedMessageImageStyles({
              width: dimensions.width,
            })}
          />
          {!count && attachment.mimeType.startsWith('video') ? (
            <View style={styles.imageOverlayContainer}>
              <Image
                style={styles.videoThumbnail}
                source={require('assets/chat/play.png')}
                contentFit="contain"
              />
            </View>
          ) : null}
          {count ? (
            <View style={styles.imageOverlayContainer}>
              <NonSelectableText
                style={styles.countText}
              >{`+ ${count}`}</NonSelectableText>
            </View>
          ) : null}
        </View>
      </Pressable>
    </PressableOpacity>
  )
}

interface EmojiProps {
  reaction: Emoji
  count: number | undefined
  messageId: string | undefined
  onShowReactors(messageId: string, reaction: Emoji): void
  onReactMessageAction(messageId: String, emoji: Emoji): void
}
function Emojis({
  reaction,
  count,
  messageId,
  onShowReactors,
  onReactMessageAction,
}: EmojiProps) {
  const styles = useStyles()
  if (!messageId) {
    return null
  }
  return (
    <>
      <PressableOpacity
        onPress={() => onReactMessageAction(messageId, reaction)}
        onLongPress={() => onShowReactors(messageId, reaction)}
      >
        <View style={styles.emoji}>
          <NonSelectableText style={styles.emojiIcon}>
            {reaction}
          </NonSelectableText>
          <NonSelectableText style={styles.count}>{count}</NonSelectableText>
        </View>
      </PressableOpacity>
    </>
  )
}

interface UserPositionsProps {
  userPositions: string[] | undefined
}

function UserPositions({ userPositions }: UserPositionsProps) {
  const styles = useStyles()
  const initialPositions = userPositions?.slice(0, POSITIONS_MAX_COUNT)

  return userPositions && initialPositions ? (
    <NonSelectableText style={styles.italicize}>
      {`${initialPositions.join(', ')} ${
        userPositions.length > POSITIONS_MAX_COUNT
          ? `${t('andMore', {
              count: userPositions.length - POSITIONS_MAX_COUNT,
            })}`
          : ``
      }`}
    </NonSelectableText>
  ) : null
}

interface ReadMoreTextProps {
  message: Message | undefined
  parsePatterns: any
  onMessageLongPress(): void
}

export function ReadMoreText({
  message,
  parsePatterns,
  onMessageLongPress,
}: ReadMoreTextProps) {
  const [collapsed, setCollapsed] = useState(true)
  const styles = useStyles()
  if (Platform.OS === 'web') {
    return (
      <PressableOpacity onPress={() => setCollapsed(!collapsed)}>
        <ParsedText
          style={styles.text}
          parse={parsePatterns}
          numberOfLines={collapsed ? 3 : undefined}
          selectable={false}
          textBreakStrategy="simple"
        >
          {stripInvalidCodepoints(message?.body ?? '')}
        </ParsedText>
      </PressableOpacity>
    )
  }
  return (
    <PressableOpacity
      onPress={() => setCollapsed(!collapsed)}
      onLongPress={onMessageLongPress}
    >
      <ReadMore
        style={styles.text}
        seeMoreText="more"
        seeMoreStyle={styles.italicize}
        seeLessStyle={styles.italicize}
        expandOnly
        animate
        collapsed={collapsed}
        onSeeMore={() => setCollapsed(false)}
      >
        <ParsedText
          style={styles.text}
          parse={parsePatterns}
          textBreakStrategy="simple"
        >
          {stripInvalidCodepoints(message?.body ?? '')}
        </ParsedText>
      </ReadMore>
    </PressableOpacity>
  )
}

export const useStyles = makeStyles(theme => ({
  container: {
    flex: 1,
  },
  card: {
    flex: 1,
    backgroundColor:
      theme.mode === 'dark' ? theme.colors?.grey10 : theme.colors?.grey4,
    padding: NEWSFEED_CARD_PADDING,
    marginVertical: 5,
  },
  avatarContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 5,
    marginBottom: 5,
  },
  emoji: {
    flex: 1,
    flexDirection: 'row',
    padding: 3,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emojiIcon: {
    fontSize: 22,
    color: 'black',
  },
  emojisContainer: { flex: 1, flexDirection: 'row', flexWrap: 'wrap' },
  text: {
    color: theme.colors.black,
    fontSize: 13,
    marginLeft: 2,
  },
  createdAtContainer: {
    alignItems: 'flex-end',
    margin: 5,
    marginBottom: 0,
  },
  nameContainer: {
    marginHorizontal: 10,
    flex: 1,
  },
  italicize: { fontStyle: 'italic' },
  name: { fontSize: 16 },
  readByContainer: { justifyContent: 'center' },
  messageBody: {
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey14 : theme.colors.white,
    padding: 10,
  },
  count: {
    fontSize: 14,
    top: 2,
    marginLeft: 4,
    marginRight: 4,
    color: theme.colors.black,
  },
  videoThumbnail: {
    width: 60,
    height: 60,
    resizeMode: 'contain',
  },
  messageImageContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 5,
  },
  messageDocumentImageContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 5,
  },
  documentNameContainer: {
    position: 'absolute',
    bottom: 0,
    backgroundColor:
      theme.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.7)',
    width: '100%',
    padding: 5,
  },
  documentAttachmentsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  attachmentsLabel: {
    fontSize: 16,
    marginBottom: 5,
    textTransform: 'uppercase',
  },
  emojisAndReadByContainer: {
    flexDirection: 'row',
    backgroundColor: theme.colors?.grey14,
    padding: 5,
    marginTop: 3,
  },
  secondaryText: { alignSelf: 'flex-end', color: theme.colors.grey3 },
  imageOverlayContainer: {
    position: 'absolute',
    zIndex: 999,
    backgroundColor: 'rgba(0,0,0,0.5)',
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  countText: { fontSize: 60, color: 'white', fontWeight: 'bold' },
  newsfeedContainer: {
    flex: 1,
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey7 : theme.colors?.grey5,
    justifyContent: 'center',
  },
  descriptionBox: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: '#FFFFB7',
  },
  descriptionText: {
    flex: 1,
    color: '#4E5665',
    paddingTop: 10,
    paddingStart: 15,
    paddingBottom: 10,
    fontSize: 13,
  },
  descriptionCloseButton: {
    width: 25,
    height: 25,
    paddingTop: 5,
    textAlign: 'center',
    fontSize: 15,
    fontFamily: font.bold,
    color: theme.colors?.grey3,
  },
  errorMessage: {
    fontSize: 17,
    textAlign: 'center',
  },
  messageLoadingIndicator: {
    flex: 1,
  },

  offlineText: {
    padding: 10,
    fontSize: 15,
    fontStyle: 'italic',
    textAlign: 'center',
    textAlignVertical: 'center',
  },
  pinnedMessagesHeader: {
    height: 50,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingTop: 15,
    padding: 10,
    paddingRight: 20,
    backgroundColor: theme.mode === 'dark' ? '#121212' : '#f9f9f9',
    borderBottomColor: 'blue',
    borderBottomWidth: 0,
  },
  pinnedMessagesHeaderText: {
    flex: 1,
    color: theme.colors?.primary,
    fontSize: 20,
    fontFamily: font.bold,
    marginLeft: 5,
  },
  pinnedChatMessages: {
    zIndex: 10,
    backgroundColor:
      theme.mode === 'dark' ? theme.colors?.grey6 : theme.colors.teal,
  },
  emptyListContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    transform: Platform.OS === 'android' ? [{ scale: -1 }] : [{ scaleY: -1 }],
  },
  pinnedButtonContainer: { zIndex: 10, flexDirection: 'row' },
  pinnedMessagesModal: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  pinnedMessagesModalContainer: {
    flex: 1,
    backgroundColor: 'transparent',
    ...(Platform.OS === 'web' && { maxWidth: WEB_SCREEN_WIDTH }),
  },
  emptyFooter: { height: 70 },
  selfTimeText: { color: theme.colors?.grey9 },
  otherTimeText: {
    color: theme.mode === 'dark' ? theme.colors.grey1 : theme.colors?.grey9,
  },
}))
